import React from 'react';
import {
  Tooltip,
  IconButton,
  Typography,
  Box,
  Link,
} from '@mui/joy';
import {
  Question as HelpIcon,
  Info as InfoIcon,
  Lightbulb as TipIcon,
} from '@phosphor-icons/react';

interface HelpTooltipProps {
  title: string;
  content: string | React.ReactNode;
  icon?: 'help' | 'info' | 'tip';
  size?: 'sm' | 'md' | 'lg';
  placement?: 'top' | 'right' | 'bottom' | 'left';
  learnMoreUrl?: string;
  example?: string;
}

export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  title,
  content,
  icon = 'help',
  size = 'sm',
  placement = 'top',
  learnMoreUrl,
  example,
}) => {
  const getIcon = () => {
    switch (icon) {
      case 'info':
        return <InfoIcon />;
      case 'tip':
        return <TipIcon />;
      default:
        return <HelpIcon />;
    }
  };

  const tooltipContent = (
    <Box sx={{ maxWidth: 300 }}>
      <Typography level="title-sm" sx={{ mb: 0.5, fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Typography level="body-xs" sx={{ mb: example || learnMoreUrl ? 1 : 0 }}>
        {content}
      </Typography>
      {example && (
        <Box
          sx={{
            bgcolor: 'background.level1',
            p: 1,
            borderRadius: 'sm',
            mb: learnMoreUrl ? 1 : 0,
          }}
        >
          <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
            Example: {example}
          </Typography>
        </Box>
      )}
      {learnMoreUrl && (
        <Link
          href={learnMoreUrl}
          target="_blank"
          level="body-xs"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          Learn more →
        </Link>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement={placement}
      arrow
      enterDelay={200}
      leaveDelay={200}
      variant="outlined"
      sx={{
        maxWidth: 320,
        '& .MuiTooltip-arrow': {
          '&::before': {
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
    >
      <IconButton
        aria-label={`Help: ${title}`}
        size={size}
        variant="plain"
        sx={{
          color: 'neutral.500',
          '&:hover': {
            color: 'var(--brand-text, #7013f5)',
            bgcolor: 'transparent',
          },
          minWidth: 'auto',
          minHeight: 'auto',
          p: 0.5,
        }}
      >
        {getIcon()}
      </IconButton>
    </Tooltip>
  );
};

// Preset help tooltips for common CMS features
export const HelpTooltips = {
  SEO_TITLE: (
    <HelpTooltip
      title="Page Title (SEO)"
      content="This appears in search results and browser tabs. Keep it under 60 characters for best results."
      example="Professional Roof Repair in Dallas | ABC Roofing"
      icon="info"
    />
  ),
  
  META_DESCRIPTION: (
    <HelpTooltip
      title="Meta Description"
      content="This shows up under your title in Google search results. Write a compelling description that makes people want to click."
      example="Expert roof repair services in Dallas. 24/7 emergency service, free estimates, and 20+ years experience. Call (555) 123-4567."
      icon="info"
    />
  ),
  
  SERVICE_AREAS: (
    <HelpTooltip
      title="Service Areas"
      content="Add all cities and neighborhoods you serve. This helps you rank in local searches like 'roofer near me' or 'Dallas roofing contractor'."
      icon="tip"
      learnMoreUrl="/help/local-seo"
    />
  ),
  
  KEYWORDS: (
    <HelpTooltip
      title="SEO Keywords"
      content="These are terms your customers search for. Our AI uses these to create content that ranks well in Google."
      example="roof repair, emergency roofing, shingle replacement"
      icon="help"
    />
  ),
  
  SPECIAL_OFFERS: (
    <HelpTooltip
      title="Special Offers"
      content="Limited-time offers create urgency and increase conversions by up to 332%. Update these regularly for best results."
      icon="tip"
    />
  ),
  
  TESTIMONIALS: (
    <HelpTooltip
      title="Customer Reviews"
      content="Social proof is crucial - 88% of consumers trust online reviews as much as personal recommendations."
      icon="info"
    />
  ),
  
  PHOTOS: (
    <HelpTooltip
      title="Project Photos"
      content="Before/after photos build trust and showcase your work quality. Tag photos with 'marketing' in the Jobs section to use them here."
      icon="help"
    />
  ),
  
  THEME: (
    <HelpTooltip
      title="Website Theme"
      content="Your theme controls the overall look and feel. You can change this anytime without losing your content."
      icon="info"
    />
  ),
  
  AI_TONE: (
    <HelpTooltip
      title="AI Writing Tone"
      content={
        <Box>
          <Typography level="body-xs" sx={{ mb: 1 }}>Choose how the AI writes your content:</Typography>
          <Typography level="body-xs">• Professional: Builds trust with expertise</Typography>
          <Typography level="body-xs">• Friendly: Warm and approachable</Typography>
          <Typography level="body-xs">• Urgent: Drives immediate action</Typography>
          <Typography level="body-xs">• Educational: Informs and guides</Typography>
        </Box>
      }
      icon="help"
    />
  ),
};
